/* global _:false */

import { AppStateFlow } from '../../app.flow';
import _ from 'lodash';
import { Features } from '../config/feature.constants';

export class BaseController {
  constructor(injections, options) {
    // Assign given injections to the controllers instance
    this.constructor.injectArguments(this, injections, options);

    // Ensure that the mandatory controllerSevice has been injected
    this.constructor.contractInjection(this, 'controllerService');

    // Set initial languageOptions once
    if (!this.languageOptions) {
      this.setLanguageOptions();
    }
  }

  get $log() {
    return this.controllerService.$log;
  }

  get $locale() {
    return this.controllerService.$locale;
  }

  get Features() {
    return Features;
  }

  get $translate() {
    return this.controllerService.$translate;
  }

  get $state() {
    return this.controllerService.$state;
  }

  get $stateParams() {
    return this.controllerService.$stateParams;
  }

  get $window() {
    return this.controllerService.$window;
  }

  get $timeout() {
    return this.controllerService.$timeout;
  }

  get isMobile() {
    return this.$window.innerWidth < 1000;
  }

  get config() {
    return this.appConfig.get();
  }

  get appConfig() {
    return this.controllerService.appConfig;
  }

  get appCache() {
    return this.controllerService.appCache;
  }

  get envService() {
    return this.controllerService.envService;
  }

  get errorService() {
    return this.controllerService.errorService;
  }

  get trackingService() {
    return this.controllerService.trackingService;
  }

  get custom() {
    return this.envService.getCustom();
  }

  get environment() {
    return this.envService.getEnvironment();
  }

  get tenant() {
    // Cut off '-depotwechsel' suffix from host
    return this.environment.tenant.replace(/-depotwechsel$/gi, '');
  }

  get isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }

  get currentState() {
    this.constructor.contractInjection(this, '$state');
    return this.$state.current;
  }

  isFeatureActive(feature) {
    return _.some(this.featureFlags, (ff) => ff.name === feature);
  }

  error(error) {
    this.constructor.contractInjection(this, 'errorService');
    this.errorService.error(error);
  }

  getConfig() {
    return this.config;
  }

  // FIXME yay, magic numbers!
  getMinMaxDate() {
    let minDate = new Date(Date.now());
    minDate.setHours(0);
    minDate.setMinutes(0);
    minDate.setSeconds(0);
    minDate.setMilliseconds(0);
    minDate.setFullYear(minDate.getFullYear() - 18);

    let initDate = new Date(Date.now());
    initDate.setHours(1);
    initDate.setMinutes(0);
    initDate.setSeconds(0);
    initDate.setMilliseconds(0);
    initDate.setFullYear(minDate.getFullYear());

    let maxDate = new Date(Date.now());
    maxDate.setFullYear(maxDate.getFullYear() - 120);

    return {
      initDate,
      minDate,
      maxDate,
    };
  }

  getConfigItem(key, defaultValue) {
    // Support tenant specific config keys, defaults to config key without tenant prefix
    const tenantKey = this.tenant + '.' + key;
    return _.get(this.config, tenantKey, _.get(this.config, key, defaultValue));
  }

  go(to, params = {}, options = {}) {
    this.constructor.contractInjection(this, '$state');

    // Ensure that the given parameters are splitted
    // to the target state and its parameters. If the
    // given to parameter contains a state object, we
    // have to deconstruct this object first.
    if (to.state) {
      params = _.merge(to.params || {}, params);
      to = to.state.name;
    }

    if (options.silent) {
      return this.$state
        .go(to, params, { location: 'replace' })
        .then(this.resetTabIndex);
    }
    return this.$state.go(to, params).then(this.resetTabIndex);
  }

  resetTabIndex() {
    // eslint-disable-next-line angular/document-service
    const firstTabElement = document.getElementById('firstTabableElement');
    firstTabElement.focus({ focusVisible: false });
  }

  goBack() {
    this.constructor.contractInjection(this, '$window');
    this.$window.history.back();

    // Support native app history
    if (navigator && navigator.app) {
      navigator.app.backHistory();
    }
  }

  get pageStates() {
    const hasStatusPage = this.configService.isFeatureActive(
      this.Features.hasStatusPage,
    );
    return Object.keys(AppStateFlow(hasStatusPage));
  }

  nextState(state) {
    if (state) {
      return this.go(state);
    }

    const nextStateIndex =
      this.pageStates.indexOf(this.$state.current.name) + 1;
    if (nextStateIndex < this.pageStates.length) {
      this.go(this.pageStates[nextStateIndex]);
    }
  }

  prevState(state) {
    if (state) {
      return this.go(state);
    }

    const prevStateIndex =
      this.pageStates.indexOf(this.$state.current.name) - 1;
    if (prevStateIndex >= 0) {
      this.go(this.pageStates[prevStateIndex]);
    }
  }

  reload(params) {
    this.go(this.$state.current, params);
  }

  bindTranslationChange(rootScope) {
    rootScope.$on('$translatePartialLoaderStructureChanged', () => {
      this.setLanguageOptions();
    });
  }

  setLanguageOptions() {
    const lang = this.$translate.use();
    return this.controllerService.tmhDynamicLocale.set(lang).then(() => {
      const { longDate, mediumDate, shortDate } = this.$locale.DATETIME_FORMATS;
      this.languageOptions = {
        lang,
        dateFormat: mediumDate,
        longDate,
        mediumDate,
        shortDate,
      };
      return this.languageOptions;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/common/base/controller.js